/**
 * Keeping a reseller's brand colour legible where we put it.
 *
 * The portal footer is near-black (gray-900) and its headings were painted in the portal's
 * raw primary colour. That works for a bright brand and fails for a dark one — Lyra's
 * purple on gray-900 is roughly the same tone as the background, so the headings read as
 * smudges. Rather than abandon the brand colour on dark surfaces, lift it until it has
 * enough light against them, which keeps the brand present AND readable.
 */

function parseHex(hex: string): [number, number, number] | null {
  const h = (hex || '').trim().replace('#', '')
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  if (full.length !== 6 || /[^0-9a-f]/i.test(full)) return null
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

const toHex = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const rgb = parseHex(hex)
  if (!rgb) return 1
  const [r, g, b] = rgb.map(v => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * A full 50–900 Tailwind-style ramp built from ONE brand colour.
 *
 * A reseller gives us a single hex. The app, though, is written against a ten-step scale
 * (`bg-primary-50`, `text-primary-600`, `from-primary-500`…), so a single colour cannot
 * paint it — which is why portals fell back to Silux green everywhere except the handful
 * of pages that used inline styles. Light steps mix toward white, dark steps toward black,
 * with the brand colour itself landing on 500.
 *
 * Returned as "r g b" channel triplets, the form Tailwind's
 * rgb(var(--x) / <alpha-value>) needs so opacity utilities keep working.
 */
export function brandRamp(hex: string): Record<number, string> {
  const rgb = parseHex(hex)
  if (!rgb) return {}
  const mix = (t: number, target: number) =>
    rgb.map(c => Math.round(c + (target - c) * t)).join(' ')
  return {
    50: mix(0.92, 255),
    100: mix(0.82, 255),
    200: mix(0.62, 255),
    300: mix(0.38, 255),
    400: mix(0.18, 255),
    500: rgb.join(' '),
    600: mix(0.08, 0),
    700: mix(0.22, 0),
    800: mix(0.38, 0),
    900: mix(0.52, 0),
  }
}

/**
 * The brand colour, lightened just enough to sit on a dark surface.
 *
 * Already-light brands are returned untouched, so a reseller whose colour works keeps the
 * exact hex they chose.
 */
export function onDark(hex: string, minLuminance = 0.45): string {
  const rgb = parseHex(hex)
  if (!rgb) return '#ffffff'
  let [r, g, b] = rgb
  // Mix toward white in small steps until it is light enough to read.
  for (let i = 0; i < 24 && luminance(`#${toHex(r)}${toHex(g)}${toHex(b)}`) < minLuminance; i++) {
    r += (255 - r) * 0.12
    g += (255 - g) * 0.12
    b += (255 - b) * 0.12
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}
